import { useMutation, useQueryClient } from '@tanstack/react-query'
import { unwrapLeaveGroupResponse, type LeaveGroupResponse } from './useLeaveGroup'

/**
 * Removes another member from a team (admin/owner "kick") via the same
 * `removeUserFromGroup` Firebase callable (europe-west1) that
 * {@link useLeaveGroup} uses for self-removal — the only difference is the uid
 * passed is the TARGET member's, not the caller's. The callable treats
 * caller == target as self-removal (no role required) and enforces the
 * owner/admin requirement server-side when caller != target, and it drops the
 * removed user's dogs and `userIds` entry. Mobile removes members the same way
 * (`removeUserFromGroup`, manage_members_bloc).
 *
 * On success the team's member list is invalidated so the removal actually
 * sticks. Web previously filtered the member out of local state only and never
 * called any backend, so a page refresh restored them (the reported bug).
 *
 * Mirrors {@link useLeaveGroup}: the Functions SDK is imported lazily to keep
 * it off the synchronous bundle path, and the `{ status }` envelope is
 * unwrapped into success or a thrown error the mutation layer can surface.
 */
export function useRemoveMember(groupId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId }: { userId: string }): Promise<void> => {
      const [{ getApp }, { getFunctions, httpsCallable }] = await Promise.all([
        import('firebase/app'),
        import('firebase/functions'),
      ])
      const fns = getFunctions(getApp(), 'europe-west1')
      const call = httpsCallable<{ groupId: string; userId: string }, LeaveGroupResponse>(
        fns,
        'removeUserFromGroup',
      )
      const res = await call({ groupId, userId })
      unwrapLeaveGroupResponse(res.data)
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['clubhouse', 'groups', groupId, 'members'] })
    },
  })
}
